import {onError} from '@apollo/client/link/error';
import type {ErrorResponse} from '@apollo/client/link/error';

import {ACTING_AS_FORBIDDEN, ACTING_AS_HEADER, handleActingAsRefused} from 'lib/actingAsHeader';
import {getGraphQLErrorCode, getResponseStatus} from 'lib/normalizeError';

/**
 * Whether the API refused the request's `Albert-Acting-As` header: a 403 whose errors carry
 * {@link ACTING_AS_FORBIDDEN}. `onError` copies a failed response's `errors` into `graphQLErrors`.
 */
export function isActingAsForbidden(
  response: Pick<ErrorResponse, 'networkError' | 'graphQLErrors'>
): boolean {
  return (
    getResponseStatus(response) === 403 &&
    (response.graphQLErrors ?? []).some(
      (error) => getGraphQLErrorCode(error) === ACTING_AS_FORBIDDEN
    )
  );
}

/**
 * Ends the masquerade when the API refuses the `Albert-Acting-As` header, as "Back to my account"
 * does. The refused operation is not retried: ending the masquerade resets the Apollo store, which
 * refetches every active query as the real user, and a mutation must not be replayed on the
 * admin's own account. Sits outside the auth link so it can read the header the request sent.
 */
const actingAsForbiddenLink = onError((response) => {
  if (isActingAsForbidden(response)) {
    handleActingAsRefused(response.operation.getContext().headers?.[ACTING_AS_HEADER] ?? null);
  }
});

export default actingAsForbiddenLink;
